/**
 * Centralized API endpoint configuration
 * All API paths should be defined here for easy management and maintenance
 */

const API_VERSION = '/api/v1';

export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: `${API_VERSION}/auth/login`,
    SIGNUP: `${API_VERSION}/auth/signup`,
    LOGOUT: `${API_VERSION}/auth/logout`,
    REFRESH_TOKEN: `${API_VERSION}/auth/refresh-token`,
    VERIFY_OTP: `${API_VERSION}/auth/verify-otp`,
    RESEND_OTP: `${API_VERSION}/auth/resend-otp`,
    ACTIVATE: `${API_VERSION}/auth/activate`,
    CHANGE_PASSWORD: `${API_VERSION}/auth/change-password`,
    PASSWORD_RESET_REQUEST: `${API_VERSION}/auth/password-reset/request`,
    PASSWORD_RESET_COMPLETE: `${API_VERSION}/auth/password-reset/complete`,
  },

  // Consent endpoints (GDPR)
  CONSENT: {
    // Unified withdraw / re-consent action: { accepted, reason? }
    MODIFY: `${API_VERSION}/consent/modify`,
  },

  // User endpoints
  USER: {
    PROFILE: `${API_VERSION}/user/profile`,
    DELETE_ACCOUNT: `${API_VERSION}/user/me`,
  },

  // Speech Service endpoints
  SPEECH: {
    TOKEN: `${API_VERSION}/speech-token`,
  },

  // Journal endpoints
  JOURNAL: {
    CREATE: `${API_VERSION}/journal`,
  },

  // Privacy Center (user data-access) endpoints
  PRIVACY: {
    JOURNALS: `${API_VERSION}/journal`,
    JOURNAL_DETAIL: (id: string) => `${API_VERSION}/journal/${id}`,
    JOURNAL_DOWNLOAD: (id: string) => `${API_VERSION}/journal/${id}/download`,
    CHAT_SESSIONS: `${API_VERSION}/user-session/list`,
    CHAT_DETAIL: (id: string) => `${API_VERSION}/chat/sessions/${id}`,
    CHAT_DOWNLOAD: (id: string) =>
      `${API_VERSION}/chat/sessions/${id}/download`,
    REVIEWS: `${API_VERSION}/chat/reviews`,
    REVIEW_DETAIL: (id: string) => `${API_VERSION}/chat/reviews/${id}`,
    REVIEW_DOWNLOAD: (id: string) =>
      `${API_VERSION}/chat/reviews/${id}/download`,
  },

  // Session endpoints
  SESSION: {
    CREATE: `${API_VERSION}/user-session`,
    DELETE: (sessionId: string) => `${API_VERSION}/user-session/${sessionId}`,
    LIST: `${API_VERSION}/user-session`,
  },

  // Chat endpoints
  CHAT: {
    SEND: `${API_VERSION}/chat/send`,
    MESSAGES: `${API_VERSION}/chat/messages`,
    ACCEPT_JOURNAL: `${API_VERSION}/chat/accept-journal`,
    ACCEPT_REVIEW: `${API_VERSION}/chat/accept-review`,
    REQUEST_PROFESSIONAL_REVIEW: `${API_VERSION}/chat/request-professional-review`,
    FEEDBACK: `${API_VERSION}/chat/feedback`,
  },

  // Admin endpoints
  ADMIN: {
    USER_SESSIONS: `${API_VERSION}/admin/user-sessions`,
    CHAT_MESSAGES: `${API_VERSION}/admin/chat-messages`,
    EXPORT_CHAT_MESSAGES: `${API_VERSION}/admin/export-chat-messages`,
    USERS: `${API_VERSION}/admin/users`,
    DEACTIVATE_USER: (userId: string) =>
      `${API_VERSION}/admin/users/${userId}/deactivate`,
    REACTIVATE_USER: (userId: string) =>
      `${API_VERSION}/admin/users/${userId}/reactivate`,
    RESEND_INVITATION: (userId: string) =>
      `${API_VERSION}/admin/users/${userId}/resend-invitation`,
    EDIT_USER: (userId: string) => `${API_VERSION}/admin/users/${userId}`,
    UNLOCK_USER: (userId: string) =>
      `${API_VERSION}/admin/users/${userId}/unlock`,
    REVIEWS_FEEDBACK: `${API_VERSION}/admin/reviews/feedback`,
    REVIEW_FEEDBACK_DETAIL: (reviewId: string) =>
      `${API_VERSION}/admin/reviews/${reviewId}/feedback`,
    ANALYTICS: {
      FEEDBACK_COUNTS: `${API_VERSION}/admin/analytics/feedback-counts`,
      USER_FEEDBACK_COUNTS: `${API_VERSION}/admin/analytics/user-feedback-counts`,
      AI_COST: `${API_VERSION}/admin/analytics/ai-cost`,
    },
    QUOTAS: `${API_VERSION}/admin/quotas`,
    QUOTA_BY_ID: (id: string) => `${API_VERSION}/admin/quotas/${id}`,
    AGENT_BOUNDARIES: `${API_VERSION}/admin/agent-boundaries`,
    AGENT_BOUNDARY_BY_ID: (id: string) =>
      `${API_VERSION}/admin/agent-boundaries/${id}`,
    AGENT_BOUNDARY_ACTIVATE: (id: string) =>
      `${API_VERSION}/admin/agent-boundaries/${id}/activate`,
    AGENT_BOUNDARY_DEACTIVATE: (id: string) =>
      `${API_VERSION}/admin/agent-boundaries/${id}/deactivate`,
    // Adversarial evaluation policies. A policy record *is* an evaluation run:
    // it carries its own status, boundaries snapshot and AI results.
    POLICIES: `${API_VERSION}/admin/policies`,
    POLICY_BY_ID: (id: string) => `${API_VERSION}/admin/policies/${id}`,
    // Returns a presigned SAS URL to the report JSON, not the file itself —
    // see admin.service.ts::downloadEvaluationPolicyReport.
    POLICY_DOWNLOAD: (id: string) =>
      `${API_VERSION}/admin/policies/${id}/download`,
    VALIDATION: {
      STATS: `${API_VERSION}/admin/validation/dashboard/stats`,
      QUERIES: `${API_VERSION}/admin/validation/queries`,
      QUERY_DETAIL: (messageId: string) =>
        `${API_VERSION}/admin/validation/queries/${messageId}/detail`,
    },
    CONFIG: {
      EVAL_DIMENSIONS: `${API_VERSION}/admin/config/evaluation/dimensions`,
      EVAL_DIMENSION: (id: string) =>
        `${API_VERSION}/admin/config/evaluation/dimensions/${id}`,
      DATA_RETENTION: `${API_VERSION}/admin/data-retention`,
    },
    AUDIT: {
      EXPORT_QUERY_LIFECYCLES: `${API_VERSION}/admin/export/query-lifecycles`,
      EXPORT_ADMIN_ACTIONS: `${API_VERSION}/admin/audit/admin-actions/export`,
    },
    KNOWLEDGE: {
      INITIATE_UPLOAD: `${API_VERSION}/knowledge/admin/initiate-upload`,
      CONFIRM_UPLOAD: `${API_VERSION}/knowledge/admin/confirm-upload`,
      SOURCES: `${API_VERSION}/knowledge/admin/sources`,
      LINKS: `${API_VERSION}/knowledge/admin/links`,
      SOURCE_DETAIL: (id: string) =>
        `${API_VERSION}/knowledge/admin/sources/${id}`,
      SOURCE_VERSION_RETRY: (versionId: string) =>
        `${API_VERSION}/knowledge/admin/sources/versions/${versionId}/retry`,
      SOURCE_VERSION_DELETE: (versionId: string) =>
        `${API_VERSION}/knowledge/admin/sources/versions/${versionId}`,
      DOMAINS: `${API_VERSION}/knowledge/admin/domains`,
      DOMAIN: (id: string) => `${API_VERSION}/knowledge/admin/domains/${id}`,
    },
  },

  // Example: Generic CRUD endpoints (from api.service.ts)
  USERS: {
    BASE: '/users',
    BY_ID: (id: number | string) => `/users/${id}`,
  },

  // Location endpoints
  LOCATION: {
    COUNTRIES: `${API_VERSION}/location/countries`,
    STATES_BY_CODE: (countryCode: string) =>
      `${API_VERSION}/location/countries/code/${countryCode}/states`,
  },

  // Reviewer endpoints
  REVIEWER: {
    REVIEWS: `${API_VERSION}/reviewer/reviews`,
    REVIEW_DETAIL: (reviewId: string) =>
      `${API_VERSION}/reviewer/reviews/${reviewId}/details`,
    ASSIGN: `${API_VERSION}/reviewer/reviews/assign`,
    LEAVE: `${API_VERSION}/reviewer/reviews/leave`,
    APPROVE: (reviewId: string) =>
      `${API_VERSION}/reviewer/reviews/${reviewId}/approve`,
    MODIFY: (reviewId: string) =>
      `${API_VERSION}/reviewer/reviews/${reviewId}/modify`,
    REJECT: (reviewId: string) =>
      `${API_VERSION}/reviewer/reviews/${reviewId}/reject`,
    FEEDBACK: (reviewId: string) =>
      `${API_VERSION}/reviewer/reviews/${reviewId}/feedback`,
    DASHBOARD_METRICS: `${API_VERSION}/reviewer/dashboard/metrics`,
  },

  // GDPR endpoints
  GDPR: {
    DISCLOSURES: {
      HUMAN_REVIEW: `${API_VERSION}/disclosures/human-review`,
    },
  },

  // Notification endpoints
  NOTIFICATIONS: {
    LIST: (page = 1, pageSize = 20) =>
      `${API_VERSION}/notifications?page=${page}&pageSize=${pageSize}`,
    MARK_READ: (id: string) => `${API_VERSION}/notifications/${id}/read`,
  },
} as const;

// Export individual endpoint groups for convenience
export const {
  AUTH,
  CONSENT,
  USER,
  USERS,
  SPEECH,
  JOURNAL,
  SESSION,
  CHAT,
  ADMIN,
  GDPR,
  LOCATION,
  REVIEWER,
  NOTIFICATIONS,
  PRIVACY,
} = API_ENDPOINTS;
